import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { engine, parseCell, tabToAscii, tabToMidiBytes, sectionMeasures, VOICE_ORDER, VOICE_LABELS } from "@/lib/drumAudio";
import { NotationKey } from "@/components/NotationKey";
import { useSticking } from "@/lib/useSticking";
import api from "@/lib/api";
import { jsPDF } from "jspdf";
import { TeacherNotes } from "@/components/TeacherNotes";
import { TapSync } from "@/components/TapSync";
import { SheetMusic } from "@/components/SheetMusic";
import { ShareSocial } from "@/components/ShareSocial";
import { ClipMaker } from "@/components/ClipMaker";
import { toast } from "sonner";
import {
  Play, Pause, RotateCcw, Repeat, Volume2, Star, Copy, Save, Music2,
  ThumbsUp, ThumbsDown, Download, Printer, SlidersHorizontal, Share2, Waves, Crown, MousePointerClick,
  Flag, Gauge, FileText, Clock, Target, Rewind, Timer, Trophy, Flame, ClipboardList, Users, RefreshCw, ZoomIn, ZoomOut, Maximize2, Minimize2, ChevronLeft, ChevronRight,
} from "lucide-react";

const ZOOMS = [1, 1.25, 1.5, 2, 2.5];

const VOICE_COLORS = {
  CC: "#FF3366", RD: "#FF8A00", HH: "#FFD400", HT: "#00F0FF",
  MT: "#00C2FF", FT: "#4D7CFF", SD: "#F0F6FC", BD: "#A855F7",
  CB: "#34D399", TM: "#A3E635", CP: "#F472B6", RS: "#2DD4BF", RB: "#FDE047",
};

function weekKey(d = new Date()) {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const ys = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const wk = Math.ceil((((dt - ys) / 86400000) + 1) / 7);
  return `${dt.getUTCFullYear()}-W${wk}`;
}

function weekStart(d = new Date()) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // 0 = Monday
  dt.setDate(dt.getDate() - day);
  return dt.toISOString().slice(0, 10);
}

export default function DrumTabViewer({ tab, user, isPro = true, onUpgrade = () => {}, onRequireAuth, onSaved, onRegenerate, sharedNote, sharedSticking, onPracticeFlush }) {
  const spm = tab.steps_per_measure || 16;
  const beats = parseInt(tab.time_signature, 10) || 4;
  const stepsPerBeat = Math.max(1, Math.round(spm / beats));

  const { sections, totalSteps, flat, usedVoices, stepRatio, stepMeta } = useMemo(() => {
    const baseBpm = tab.bpm || 120;
    let global = 0;
    const ratios = [];
    const meta = [];
    const secs = tab.sections.map((sec) => {
      const secBpm = sec.bpm || baseBpm;
      const endBpm = sec.ramp || secBpm;
      const ease = sec.rampCurve === "ease";
      const srcMeasures = sectionMeasures(sec);
      const secSteps = srcMeasures.length * spm;
      let j = 0;
      const measures = srcMeasures.map((m) => {
        const startStep = global;
        const n = m.steps || spm;
        for (let i = 0; i < n; i++) {
          const frac = secSteps > 1 ? Math.min(1, (j + (i * spm) / n) / (secSteps - 1)) : 0;
          const ef = ease ? frac * frac * (3 - 2 * frac) : frac;
          const stepBpm = secBpm + (endBpm - secBpm) * ef;
          ratios[global + i] = (stepBpm / baseBpm) * (n / spm);
          const mspb = Math.max(1, Math.round(n / beats));
          meta[global + i] = [i % mspb, Math.floor(i / mspb), mspb];
        }
        j += spm;
        global += n;
        return { lanes: m.lanes, startStep, steps: n, src: m.src, fill: m.fill };
      });
      return { name: sec.name, measures, bpm: secBpm, base: (sec.measures || []).length, endLen: measures.length ? Math.max(0, ...measures.map((x) => x.src)) + 1 - (sec.measures || []).length : 0, repeat: Math.max(1, Math.min(16, parseInt(sec.repeat, 10) || 1)) };
    });
    const total = global;

    const used = VOICE_ORDER.filter(
      (v) =>
        ["HH", "SD", "BD"].includes(v) ||
        tab.sections.some((s) =>
          s.measures.concat(s.ending || []).some((m) => (m.lanes[v] || "").split("").some((c) => parseCell(c).hit))
        )
    );

    const flatArr = Array.from({ length: total }, () => []);
    secs.forEach((sec) => {
      sec.measures.forEach((m) => {
        for (let i = 0; i < m.steps; i++) {
          used.forEach((v) => {
            const cell = parseCell((m.lanes[v] || "")[i]);
            if (cell.hit) {
              flatArr[m.startStep + i].push({ voice: v, accent: cell.accent, open: cell.open && v === "HH", ghost: cell.ghost });
            }
          });
        }
      });
    });
    return { sections: secs, totalSteps: total, flat: flatArr, usedVoices: used, stepRatio: ratios, stepMeta: meta };
  }, [tab, spm, beats]);

  const [bpm, setBpm] = useState(tab.bpm || 120);
  const [playing, setPlaying] = useState(false);
  const activeStepRef = useRef(-1);
  const followRef = useRef(true);
  // Playhead highlight is applied directly to the DOM so a long song never re-renders
  // thousands of cells per step (which made the highlight lag behind the sound).
  const setActiveStep = useCallback((n) => {
    if (activeStepRef.current === n) return;
    activeStepRef.current = n;
    let el = null;
    if (sheetApiRef.current) {
      el = sheetApiRef.current.highlight(n);
      if (n < 0) return;
    } else {
      const root = scrollRef.current;
      if (!root) return;
      root.querySelectorAll(".tab-cell.is-active").forEach((e) => e.classList.remove("is-active"));
      if (n < 0) { activeCellRef.current = null; return; }
      const cells = root.querySelectorAll(`[data-step="${n}"]`);
      cells.forEach((e) => e.classList.add("is-active"));
      el = cells[0];
    }
    activeCellRef.current = el || null;
    if (!el || !followRef.current) return;
    const r = el.getBoundingClientRect();
    const box = fsBoxRef.current;
    const row = el.closest("[data-tab-row]") || el;
    if (box) {
      const b = box.getBoundingClientRect();
      const barH = box.querySelector("[data-fs-bar]")?.offsetHeight || 0;
      if (r.top < b.top + barH + 8 || r.bottom > b.bottom - 16) {
        box.scrollBy({ top: row.getBoundingClientRect().top - b.top - barH - 12, behavior: "auto" });
      }
      return;
    }
    const top = 96;
    if (r.top < top || r.bottom > window.innerHeight - 24) {
      window.scrollBy({ top: row.getBoundingClientRect().top - top - 16, behavior: "auto" });
    }
  }, []);
  const fsBoxRef = useRef(null);
  const gridSectionRef = useRef(null);
  const [fullScreen, setFullScreen] = useState(false);
  const toggleFullScreen = () => setFullScreen((f) => !f);
  useEffect(() => {
    fsBoxRef.current = fullScreen ? gridSectionRef.current : null;
    const docEl = document.documentElement;
    if (fullScreen) {
      document.body.style.overflow = "hidden";
      if (docEl.requestFullscreen && !document.fullscreenElement) docEl.requestFullscreen().catch(() => {});
    } else {
      document.body.style.overflow = "";
      if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
    }
    const onKey = (e) => { if (e.key === "Escape") setFullScreen(false); };
    const onFs = () => { if (!document.fullscreenElement) setFullScreen(false); };
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("fullscreenchange", onFs); };
  }, [fullScreen]);
  useEffect(() => () => { document.body.style.overflow = ""; }, []);
  const [loop, setLoop] = useState(true);
  const [countIn, setCountIn] = useState(false);
  const [followPlayhead, setFollowPlayhead] = useState(true);
  const [zoomIdx, setZoomIdx] = useState(() => {
    const i = parseInt((typeof localStorage !== "undefined" && localStorage.getItem("beatflow_zoom")) || "0", 10);
    return Number.isFinite(i) && i >= 0 && i < ZOOMS.length ? i : 0;
  });
  const changeZoom = (d) => setZoomIdx((i) => {
    const n = Math.max(0, Math.min(ZOOMS.length - 1, i + d));
    try { localStorage.setItem("beatflow_zoom", String(n)); } catch (e) { /* noop */ }
    return n;
  });
  const [syncMs, setSyncMs] = useState(() => {
    const v = parseInt((typeof localStorage !== "undefined" && localStorage.getItem("beatflow_sync_ms")) || "0", 10);
    return Number.isFinite(v) ? Math.max(-500, Math.min(500, v)) : 0;
  });
  const syncRef = useRef(syncMs);
  const changeSync = (d) => setSyncMs((v) => {
    const n = d === 0 ? 0 : Math.max(-500, Math.min(500, v + d));
    syncRef.current = n;
    try { localStorage.setItem("beatflow_sync_ms", String(n)); } catch (e) { /* noop */ }
    return n;
  });
  const setSyncExact = (ms) => {
    syncRef.current = ms;
    setSyncMs(ms);
    try { localStorage.setItem("beatflow_sync_ms", String(ms)); } catch (e) { /* noop */ }
    toast.success(`Sync set to ${ms > 0 ? "+" : ""}${ms}ms`);
  };
  const [tapSyncOpen, setTapSyncOpen] = useState(false);
  const forceCountRef = useRef(false);
  const [volume, setVolume] = useState(90);
  const [savedId, setSavedId] = useState(null);
  const [favorite, setFavorite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loopSection, setLoopSection] = useState("all");
  const [showMixer, setShowMixer] = useState(false);
  const [mixer, setMixer] = useState({});
  const [solo, setSolo] = useState(new Set());
  const [voted, setVoted] = useState(null);
  const [accuracyStats, setAccuracyStats] = useState(null);
  const [showReasons, setShowReasons] = useState(false);
  const [customReason, setCustomReason] = useState("");
  const [teacherNote, setTeacherNote] = useState(null);
  const [swing, setSwing] = useState(0);
  const [ghostVol, setGhostVol] = useState(30);
  const ghostRef = useRef(0.3);
  const [accentVol, setAccentVol] = useState(100);
  const [mixSavedAt, setMixSavedAt] = useState(0);
  const lastMixRef = useRef(null);
  const trainerEveryRef = useRef(1);
  const trainerWrapRef = useRef(0);
  const [clipOpen, setClipOpen] = useState(false);
  const [fillSnap, setFillSnap] = useState(null);
  const [seekStep, setSeekStep] = useState(null);
  const [loopStart, setLoopStart] = useState(null);
  const [loopEnd, setLoopEnd] = useState(null);
  const [loopMarkMode, setLoopMarkMode] = useState(false);
  const [trainer, setTrainer] = useState(false);
  const [trainerStep, setTrainerStep] = useState(5);
  const [trainerTarget, setTrainerTarget] = useState("");
  const [halfSpeed, setHalfSpeed] = useState(false);
  const [practiceSecs, setPracticeSecs] = useState(0);
  const [weekSecs, setWeekSecs] = useState(0);
  const [weekGoal, setWeekGoal] = useState("");
  const [metroOnly, setMetroOnly] = useState(false);
  const [loopCount, setLoopCount] = useState(0);
  const [goalFlash, setGoalFlash] = useState(false);
  const [accentMode, setAccentMode] = useState("downbeat");
  const [sessionSecs, setSessionSecs] = useState(0);
  const [sessionLoops, setSessionLoops] = useState([]);
  const [sessionSongs, setSessionSongs] = useState([]);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [streakCount, setStreakCount] = useState(0);
  const [countNum, setCountNum] = useState(0);

  const rafRef = useRef(null);
  const startTimeRef = useRef(0);
  const spsRef = useRef(0);
  const offsetsRef = useRef([]);
  const stepRatioRef = useRef([]);
  const scheduledRef = useRef([]);
  const loopRef = useRef(loop);
  const playFromRef = useRef(null);
  const rangeStartRef = useRef(0);
  const rangeEndRef = useRef(0);
  const mixerRef = useRef({});
  const soloRef = useRef(new Set());
  const swingRef = useRef(0);
  const scrollRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(0);
  const widthRef = useRef(null);
  const sheetApiRef = useRef(null);
  const [viewMode, setViewMode] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("beatflow_view")) || "grid");
  const [swingNotation, setSwingNotation] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("beatflow_swing_notation")) !== "0");
  const [showSticking, setShowSticking] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("beatflow_sticking")) !== "0");
  const toggleSwingNotation = () => setSwingNotation((v) => { try { localStorage.setItem("beatflow_swing_notation", v ? "0" : "1"); } catch (e) { /* noop */ } return !v; });
  const [compactSheet, setCompactSheet] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("beatflow_compact")) === "1");
  const toggleCompact = () => setCompactSheet((v) => { try { localStorage.setItem("beatflow_compact", v ? "0" : "1"); } catch (e) { /* noop */ } return !v; });
  const [editSticking, setEditSticking] = useState(false);
  const { stickMap, stickSource, setStick, resetSticking } = useSticking(tab, user, sharedSticking);
  const toggleSticking = () => setShowSticking((v) => { try { localStorage.setItem("beatflow_sticking", v ? "0" : "1"); } catch (e) { /* noop */ } return !v; });
  const changeView = (v) => {
    setViewMode(v);
    try { localStorage.setItem("beatflow_view", v); } catch (e) { /* noop */ }
  };
  useEffect(() => {
    const el = widthRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setGridWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const activeCellRef = useRef(null);
  const seekRef = useRef(null);
  const beginRef = useRef(0);
  const bpmRef = useRef(tab.bpm || 120);
  const loopStartRef = useRef(null);
  const loopEndRef = useRef(null);
  const trainerRef = useRef(false);
  const trainerStepRef = useRef(5);
  const trainerTargetRef = useRef(null);
  const halfSpeedRef = useRef(false);
  const metroOnlyRef = useRef(false);
  const loopCountRef = useRef(0);
  const accentRef = useRef("downbeat");
  const sessionLoopsRef = useRef({});
  const sessionSongsRef = useRef(new Set());
  const loopLabelRef = useRef("");
  const streakDoneRef = useRef(false);
  const countTimersRef = useRef([]);

  useEffect(() => { loopRef.current = loop; }, [loop]);
  useEffect(() => { setBpm(tab.bpm || 120); setSavedId(tab._libId || null); setFavorite(false); lastMixRef.current = null; setFillSnap(null);
    if (tab._sheet) {
      setViewMode("sheet");
      setTimeout(() => widthRef.current && widthRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { stepRatioRef.current = stepRatio; }, [stepRatio]);
  useEffect(() => { trainerRef.current = trainer; }, [trainer]);
  useEffect(() => { trainerStepRef.current = trainerStep; }, [trainerStep]);
  useEffect(() => {
    const t = parseInt(trainerTarget, 10);
    trainerTargetRef.current = Number.isFinite(t) && t > 0 ? t : null;
  }, [trainerTarget]);
  useEffect(() => { halfSpeedRef.current = halfSpeed; }, [halfSpeed]);
  useEffect(() => { metroOnlyRef.current = metroOnly; }, [metroOnly]);
  useEffect(() => { accentRef.current = accentMode; }, [accentMode]);
  useEffect(() => { engine.setVolume(volume / 100); }, [volume]);

  // Practice timer: accumulate seconds spent playing today + this week (persisted).
  const pendingPracticeRef = useRef(0);
  const flushPractice = () => {
    const secs = Math.min(pendingPracticeRef.current, 600);
    pendingPracticeRef.current = 0;
    if (secs >= 1 && onPracticeFlush) onPracticeFlush(secs, tab);
    if (!user || secs < 1 || !tab?.title) return;
    api.post("/practice/log", { title: tab.title, artist: tab.artist || "", seconds: secs }).catch(() => {});
  };
  const flushRef = useRef(flushPractice);
  flushRef.current = flushPractice;
  useEffect(() => () => flushPractice(), [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  // Weekly goal lives on the account for logged-in drummers (shared with "My practice").
  useEffect(() => {
    if (user?.practice_goal) setWeekGoal(String(user.practice_goal));
    if (user) {
      api.get("/practice/stats").then((r) => {
        const w = r.data.weeks[r.data.weeks.length - 1];
        setWeekSecs((s) => Math.max(s, Math.round((w?.minutes || 0) * 60)));
      }).catch(() => {});
    }
    const onGoal = (e) => setWeekGoal(e.detail ? String(e.detail) : "");
    window.addEventListener("beatflow:goal-changed", onGoal);
    return () => window.removeEventListener("beatflow:goal-changed", onGoal);
  }, [user?.practice_goal]); // eslint-disable-line react-hooks/exhaustive-deps
  const goalSaveRef = useRef(null);
  const saveGoal = (val) => {
    setWeekGoal(val);
    if (!user) return;
    clearTimeout(goalSaveRef.current);
    goalSaveRef.current = setTimeout(() => {
      const n = parseInt(val, 10);
      api.put("/practice/goal", { minutes: Number.isFinite(n) && n > 0 ? Math.min(5000, n) : 0 }).catch(() => {});
    }, 800);
  };
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const wk = weekKey();
    try {
      const d = JSON.parse(localStorage.getItem("beatflow_practice") || "{}");
      setPracticeSecs(d.date === today && typeof d.secs === "number" ? d.secs : 0);
      const w = JSON.parse(localStorage.getItem("beatflow_week") || "{}");
      setWeekSecs(w.week === wk && typeof w.secs === "number" ? w.secs : 0);
      const g = localStorage.getItem("beatflow_week_goal");
      if (g) setWeekGoal(g);
      const s = JSON.parse(localStorage.getItem("beatflow_streak") || "{}");
      const cur = weekStart();
      const prev = weekStart(new Date(Date.now() - 7 * 86400000));
      if (s.count && (s.week === cur || s.week === prev)) setStreakCount(s.count);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => {
    const g = parseInt(weekGoal, 10);
    try { localStorage.setItem("beatflow_week_goal", Number.isFinite(g) && g > 0 ? String(g) : ""); } catch (e) { /* noop */ }
  }, [weekGoal]);
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      const today = new Date().toISOString().slice(0, 10);
      const wk = weekKey();
      setPracticeSecs((s) => {
        const n = s + 1;
        try { localStorage.setItem("beatflow_practice", JSON.stringify({ date: today, secs: n })); } catch (e) { /* noop */ }
        return n;
      });
      setWeekSecs((s) => {
        const n = s + 1;
        try { localStorage.setItem("beatflow_week", JSON.stringify({ week: wk, secs: n })); } catch (e) { /* noop */ }
        return n;
      });
      setSessionSecs((s) => s + 1);
      pendingPracticeRef.current += 1;
      if (pendingPracticeRef.current >= 30) flushRef.current();
    }, 1000);
    return () => { clearInterval(id); flushRef.current(); };
  }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Practice streak: award consecutive weeks of hitting the weekly goal.
  useEffect(() => {
    const goal = parseInt(weekGoal, 10);
    if (!(Number.isFinite(goal) && goal > 0)) return;
    if (Math.floor(weekSecs / 60) < goal) return;
    try {
      const store = JSON.parse(localStorage.getItem("beatflow_streak") || "{}");
      const cur = weekStart();
      if (store.week === cur) {
        setStreakCount(store.count || 1);
        return;
      }
      const prev = weekStart(new Date(Date.now() - 7 * 86400000));
      const count = store.week === prev ? (store.count || 0) + 1 : 1;
      localStorage.setItem("beatflow_streak", JSON.stringify({ week: cur, count }));
      setStreakCount(count);
      if (!streakDoneRef.current) {
        streakDoneRef.current = true;
        toast.success(count > 1 ? `Weekly goal hit — ${count}-week streak!` : "Weekly practice goal reached!");
      }
    } catch (e) { /* noop */ }
  }, [weekSecs, weekGoal]);
  useEffect(() => {
    const saved = tab.mix;
    const m = {};
    usedVoices.forEach((v) => {
      m[v] = saved && saved.mixer && saved.mixer[v] ? { ...saved.mixer[v] } : { vol: 1, mute: false };
    });
    setMixer(m);
    setSolo(new Set(saved && saved.solo ? saved.solo : []));
    setSwing(saved && typeof saved.swing === "number" ? saved.swing : 0);
    setGhostVol(saved && typeof saved.ghostVol === "number" ? saved.ghostVol : 30);
    setAccentVol(saved && typeof saved.accentVol === "number" ? saved.accentVol : 100);
    trainerEveryRef.current = 1;
    setLoopSection("all");
    setShowMixer(false);
    setVoted(null);
    setSeekStep(null);
    seekRef.current = null;
    setLoopMarkMode(false);
    setCountNum(0);
    setHalfSpeed(false);
    halfSpeedRef.current = false;
    // Restore saved A–B loop + trainer settings if present on the tab.
    if (saved && saved.loopStart != null && saved.loopEnd != null) {
      setLoopStart(saved.loopStart);
      setLoopEnd(saved.loopEnd);
      loopStartRef.current = saved.loopStart;
      loopEndRef.current = saved.loopEnd;
    } else {
      setLoopStart(null);
      setLoopEnd(null);
      loopStartRef.current = null;
      loopEndRef.current = null;
    }
    const savedTrainer = !!(saved && saved.trainer);
    setTrainer(savedTrainer);
    trainerRef.current = savedTrainer;
    setTrainerStep(saved && saved.trainerStep ? saved.trainerStep : 5);
    setTrainerTarget(saved && saved.trainerTarget ? String(saved.trainerTarget) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  useEffect(() => { mixerRef.current = mixer; }, [mixer]);
  useEffect(() => { soloRef.current = solo; }, [solo]);
  useEffect(() => { swingRef.current = swing; }, [swing]);
  useEffect(() => { ghostRef.current = ghostVol / 100; }, [ghostVol]);
  useEffect(() => { engine.accentLevel = accentVol / 100; }, [accentVol]);

  useEffect(() => { followRef.current = followPlayhead; }, [followPlayhead]);

  // Lookahead scheduler: audio is queued ~0.3s ahead from a worker-driven tick, so
  // loops/repeats continue seamlessly and main-thread hiccups never shift the beat.
  const schedRef = useRef(null);
  const workerRef = useRef(null);
  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (workerRef.current) workerRef.current.postMessage("stop");
    schedRef.current = null;
    scheduledRef.current.forEach((n) => { try { n.stop(); } catch (e) { /* noop */ } });
    scheduledRef.current = [];
    countTimersRef.current.forEach((id) => clearTimeout(id));
    countTimersRef.current = [];
    setCountNum(0);
    setPlaying(false);
    setActiveStep(-1);
  }, [setActiveStep]);

  const playFrom = useCallback(() => {
    engine.ensure();
    if (engine.ctx.state === "suspended") engine.ctx.resume();
    const curBpm0 = bpmRef.current * (halfSpeedRef.current ? 0.5 : 1);
    const beatDur = 60 / curBpm0;

    let rangeStart = 0;
    let rangeEnd = totalSteps;
    const ls = loopStartRef.current;
    const le = loopEndRef.current;
    if (ls != null && le != null) {
      rangeStart = Math.min(ls, le);
      rangeEnd = Math.max(ls, le) + 1;
    } else if (loopSection !== "all" && sections[loopSection]) {
      const ms = sections[loopSection].measures;
      rangeStart = ms[0].startStep;
      rangeEnd = ms[ms.length - 1].startStep + ms[ms.length - 1].steps;
    }
    let begin = rangeStart;
    const sk = seekRef.current;
    if (sk != null && sk >= rangeStart && sk < rangeEnd) begin = sk;
    rangeStartRef.current = rangeStart;
    rangeEndRef.current = rangeEnd;
    beginRef.current = begin;

    const gainFor = (v) => {
      const m = mixerRef.current[v] || { vol: 1, mute: false };
      const soloed = soloRef.current;
      const audible = !m.mute && (soloed.size === 0 || soloed.has(v));
      return audible ? m.vol : 0;
    };
    const beatAccent = (idx) => {
      const mode = accentRef.current;
      if (mode === "all") return true;
      if (mode === "none") return false;
      if (mode === "alternate") return idx % 2 === 0;
      return idx === 0;
    };

    const doCount = countIn || forceCountRef.current;
    forceCountRef.current = false;
    const startAt = engine.now + 0.15 + (doCount ? beats * beatDur : 0);
    countTimersRef.current.forEach((id) => clearTimeout(id));
    countTimersRef.current = [];
    if (doCount) {
      for (let i = 0; i < beats; i++) engine.click(engine.now + 0.15 + i * beatDur, beatAccent(i));
      for (let i = 0; i < beats; i++) {
        countTimersRef.current.push(setTimeout(() => setCountNum(i + 1), (0.15 + i * beatDur) * 1000));
      }
      countTimersRef.current.push(setTimeout(() => setCountNum(0), (0.15 + beats * beatDur) * 1000));
    }
    const eighthSteps = Math.max(1, Math.round(stepsPerBeat / 2));
    const swingFor = (g, stepSps) => {
      const [pos, , mspb] = stepMeta[g] || [0, 0, stepsPerBeat];
      if (mspb !== stepsPerBeat) return 0;
      const eighthIndex = pos / eighthSteps;
      const swingDelay = (swingRef.current / 100) * ((eighthSteps * stepSps) / 3);
      return Number.isInteger(eighthIndex) && eighthIndex % 2 === 1 ? swingDelay : 0;
    };
    const spsNow = (g) => {
      const b = bpmRef.current * (halfSpeedRef.current ? 0.5 : 1);
      const ratios = stepRatioRef.current || [];
      return ((60 / b) * (beats / spm)) / (ratios[g] || 1);
    };

    const st = { next: begin, time: startAt, queue: [], endAt: null, wraps: 0 };
    schedRef.current = st;
    startTimeRef.current = startAt;
    scheduledRef.current = [];

    const onWrap = () => {
      trainerWrapRef.current += 1;
      if (trainerRef.current && trainerWrapRef.current >= trainerEveryRef.current) {
        trainerWrapRef.current = 0;
        const target = trainerTargetRef.current;
        let nb = Math.min(240, bpmRef.current + trainerStepRef.current);
        if (target != null && nb >= target) {
          nb = Math.min(240, target);
          trainerRef.current = false;
          setTrainer(false);
          setGoalFlash(true);
          engine.celebrate(engine.now + 0.02);
          setTimeout(() => setGoalFlash(false), 1300);
          toast.success(`Reached your ${nb} BPM goal!`);
        }
        if (nb !== bpmRef.current) { bpmRef.current = nb; setBpm(nb); }
      }
      if (loopStartRef.current != null && loopEndRef.current != null) {
        loopCountRef.current += 1;
        setLoopCount(loopCountRef.current);
        const key = `${loopStartRef.current}-${loopEndRef.current}`;
        const entry = sessionLoopsRef.current[key] || { label: loopLabelRef.current || "Loop", count: 0 };
        entry.count += 1;
        if (loopLabelRef.current) entry.label = loopLabelRef.current;
        sessionLoopsRef.current[key] = entry;
      }
    };

    const schedule = () => {
      const s = schedRef.current;
      if (!s || s !== st) return;
      const horizon = engine.now + 0.3;
      while (s.endAt == null && s.time < horizon) {
        const g = s.next;
        const stepSps = spsNow(g);
        const at = s.time + swingFor(g, stepSps);
        if (metroOnlyRef.current) {
          const [pos, bi] = stepMeta[g] || [0, 0];
          if (pos === 0) scheduledRef.current.push(...engine.click(at, beatAccent(bi)));
        } else {
          flat[g].forEach(({ voice, accent, open, ghost }) => {
            scheduledRef.current.push(...engine.trigger(open ? "OH" : voice, at, accent, gainFor(voice) * (ghost ? ghostRef.current : 1)));
          });
        }
        s.queue.push([g, s.time]);
        s.time += stepSps;
        s.next = g + 1;
        if (s.next >= rangeEnd) {
          if (loopRef.current) {
            s.next = rangeStart;
            seekRef.current = null;
            onWrap();
          } else {
            s.endAt = s.time;
          }
        }
      }
      if (scheduledRef.current.length > 400) scheduledRef.current = scheduledRef.current.slice(-200);
    };
    schedule();

    if (!workerRef.current && typeof Worker !== "undefined") {
      try {
        const code = "let id=null;onmessage=(e)=>{if(e.data==='start'){clearInterval(id);id=setInterval(()=>postMessage('tick'),25);}else{clearInterval(id);id=null;}};";
        workerRef.current = new Worker(URL.createObjectURL(new Blob([code], { type: "application/javascript" })));
      } catch (e) { workerRef.current = null; }
    }
    if (workerRef.current) {
      workerRef.current.onmessage = () => schedRef.current && schedRef.current.tick && schedRef.current.tick();
      workerRef.current.postMessage("start");
    }
    st.tick = schedule;
    setPlaying(true);

    const tick = () => {
      const s = schedRef.current;
      if (!s || s !== st) return;
      if (!workerRef.current) schedule();
      const lat = Math.min(0.12, (engine.ctx && (engine.ctx.outputLatency || engine.ctx.baseLatency)) || 0);
      const heard = engine.now - lat + syncRef.current / 1000;
      if (s.endAt != null && heard >= s.endAt) { stop(); return; }
      let cur = -1;
      while (s.queue.length && s.queue[0][1] <= heard) cur = s.queue.shift()[0];
      if (cur >= 0) setActiveStep(cur);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [beats, spm, stepsPerBeat, countIn, flat, totalSteps, stop, loopSection, sections, setActiveStep, stepMeta]);

  useEffect(() => () => { if (workerRef.current) workerRef.current.terminate(); }, []);
  useEffect(() => { playFromRef.current = playFrom; }, [playFrom]);
  useEffect(() => () => stop(), [stop]);

  const togglePlay = () => {
    if (playing) stop();
    else {
      setLoopCount(0);
      loopCountRef.current = 0;
      if (tab && tab.title) sessionSongsRef.current.add(tab.title);
      playFrom();
    }
  };

  const restart = () => {
    stop();
    setSeekStep(null);
    seekRef.current = null;
    setLoopCount(0);
    loopCountRef.current = 0;
    setTimeout(() => playFrom(), 60);
  };

  const seekTo = (global) => {
    setSeekStep(global);
    seekRef.current = global;
    if (playing) {
      stop();
      setTimeout(() => playFrom(), 50);
    } else {
      setActiveStep(global);
    }
  };

  const restartIfPlaying = () => {
    if (playing) {
      stop();
      setTimeout(() => playFrom(), 50);
    }
  };

  const setMarker = (global) => {
    if (loopStart != null && loopEnd == null) {
      // second click -> set end, finalize loop
      let a = loopStart;
      let b = global;
      if (b < a) [a, b] = [b, a];
      setLoopStart(a);
      setLoopEnd(b);
      loopStartRef.current = a;
      loopEndRef.current = b;
      loopLabelRef.current = labelForStep(a) === labelForStep(b)
        ? labelForStep(a)
        : `${labelForStep(a)} → ${labelForStep(b)}`;
      setLoopMarkMode(false);
      setLoop(true);
      loopRef.current = true;
      seekRef.current = null;
      setSeekStep(null);
      setLoopCount(0);
      loopCountRef.current = 0;
      toast.success("A–B loop set — it will repeat that range");
      restartIfPlaying();
    } else {
      // first click -> set start
      setLoopStart(global);
      setLoopEnd(null);
      loopStartRef.current = global;
      loopEndRef.current = null;
      toast.message("Loop start set — now click the end beat");
    }
  };

  const toggleLoopMark = () => {
    if (loopStart != null && loopEnd != null) {
      setLoopStart(null);
      setLoopEnd(null);
      setLoopMarkMode(false);
      loopStartRef.current = null;
      loopEndRef.current = null;
      setFillSnap(null);
      trainerEveryRef.current = 1;
      toast.message("A–B loop cleared");
      restartIfPlaying();
    } else if (loopMarkMode) {
      setLoopMarkMode(false);
      setLoopStart(null);
      loopStartRef.current = null;
    } else {
      setLoopMarkMode(true);
      setLoopStart(null);
      setLoopEnd(null);
      loopStartRef.current = null;
      loopEndRef.current = null;
      toast.message("Click the loop start beat, then the end beat");
    }
  };

  const toggleTrainer = () => {
    trainerEveryRef.current = 1;
    setFillSnap(null);
    setTrainer((t) => {
      const next = !t;
      trainerRef.current = next;
      if (next) { setLoop(true); loopRef.current = true; }
      return next;
    });
  };

  const toggleHalfSpeed = () => {
    const n = !halfSpeedRef.current;
    halfSpeedRef.current = n;
    setHalfSpeed(n);
  };

  const toggleMetroOnly = () => {
    const n = !metroOnlyRef.current;
    metroOnlyRef.current = n;
    setMetroOnly(n);
  };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const weekMin = Math.floor(weekSecs / 60);
  const weekGoalNum = parseInt(weekGoal, 10) || 0;
  const weekPct = weekGoalNum > 0 ? Math.min(100, (weekMin / weekGoalNum) * 100) : 0;

  const labelForStep = (g) => {
    for (const sec of sections) {
      for (const m of sec.measures) {
        if (g >= m.startStep && g < m.startStep + m.steps) return sec.name;
      }
    }
    return "Loop";
  };

  const openSummary = () => {
    setSessionLoops(Object.values(sessionLoopsRef.current).sort((a, b) => b.count - a.count));
    setSessionSongs(Array.from(sessionSongsRef.current));
    try { setSessionHistory(JSON.parse(localStorage.getItem("beatflow_sessions") || "[]")); } catch (e) { setSessionHistory([]); }
    setSummaryOpen(true);
  };

  const endSession = () => {
    const loopsTotal = Object.values(sessionLoopsRef.current).reduce((a, l) => a + l.count, 0);
    if (sessionSecs === 0 && sessionSongsRef.current.size === 0) {
      toast.message("Nothing to log yet — play something first!");
      return;
    }
    const entry = {
      date: new Date().toISOString(),
      secs: sessionSecs,
      songs: Array.from(sessionSongsRef.current),
      loops: loopsTotal,
    };
    let hist = [];
    try { hist = JSON.parse(localStorage.getItem("beatflow_sessions") || "[]"); } catch (e) { /* noop */ }
    hist = [entry, ...hist].slice(0, 10);
    try { localStorage.setItem("beatflow_sessions", JSON.stringify(hist)); } catch (e) { /* noop */ }
    setSessionHistory(hist);
    setSessionSecs(0);
    sessionLoopsRef.current = {};
    sessionSongsRef.current = new Set();
    loopCountRef.current = 0;
    setSessionLoops([]);
    setSessionSongs([]);
    setLoopCount(0);
    toast.success("Session logged!");
  };

  const postToClass = async () => {
    try {
      await api.post("/team/feed", { minutes: weekMin, streak: streakCount });
      toast.success("Posted to your class feed!");
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Join a class to share your progress." : "Could not post to class");
    }
  };

  const shareProgress = async () => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 1200; canvas.height = 630;
      const c = canvas.getContext("2d");
      c.fillStyle = "#0b0c10"; c.fillRect(0, 0, 1200, 630);
      c.fillStyle = "#00f0ff"; c.fillRect(0, 0, 1200, 12);
      c.fillStyle = "#00f0ff"; c.font = "bold 42px Arial"; c.fillText("BeatFlow AI", 64, 104);
      c.fillStyle = "#ffffff"; c.font = "bold 128px Arial"; c.fillText(`${weekMin} min`, 60, 310);
      c.fillStyle = "#94a3b8"; c.font = "34px Arial"; c.fillText("practiced on the drums this week", 64, 366);
      if (streakCount > 0) { c.fillStyle = "#ffab00"; c.font = "bold 42px Arial"; c.fillText(`${streakCount}-week streak`, 64, 470); }
      c.fillStyle = "#64748b"; c.font = "30px Arial"; c.fillText("Turn any song into a drum tab at BeatFlow AI", 64, 566);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "beatflow-progress.png", { type: "image/png" });
      const text = `I practiced ${weekMin} min on drums this week with BeatFlow AI!`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], text }); return; } catch (e) { /* fall through */ }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "beatflow-progress.png";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      try { await navigator.clipboard.writeText(text); } catch (e) { /* noop */ }
      toast.success("Progress card saved — share it with friends!");
    } catch (e) {
      toast.error("Could not create progress card");
    }
  };

  const downloadPdf = async () => {
    try {
      const doc = new jsPDF({ unit: "pt", format: "letter", compress: true });
      const margin = 48;
      let y = margin;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text(tab.title || "Drum Tab", margin, y);
      y += 20;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(90);
      if (tab.artist) { doc.setFontSize(12); doc.text(tab.artist, margin, y); y += 16; }
      const meta = [tab.bpm ? `${tab.bpm} BPM` : "", tab.time_signature, tab.difficulty,
        tab.key && tab.key !== "—" ? `Key: ${tab.key}` : ""].filter(Boolean).join("   .   ");
      doc.setFontSize(10);
      doc.text(meta, margin, y);
      y += 14;
      doc.setDrawColor(210);
      doc.line(margin, y, 564, y);
      y += 18;
      if (teacherNote) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(0);
        doc.text(`Teacher's tips${teacherNote.name ? ` - ${teacherNote.name}` : ""}`, margin, y);
        y += 14;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.splitTextToSize(teacherNote.note, 516).forEach((ln) => { doc.text(ln, margin, y); y += 13; });
        y += 10;
      }
      const pageH = doc.internal.pageSize.getHeight();
      const sheetEl = viewMode === "sheet" ? document.querySelector("[data-testid=sheet-music]") : null;
      if (sheetEl) {
        // Sheet music: rasterise each stave line (SVG) into the PDF at print resolution.
        const maxW = 564 - margin;
        const items = Array.from(sheetEl.children);
        for (const el of items) {
          const svg = el.querySelector && el.querySelector("svg");
          if (!svg) {
            if (y > pageH - margin - 20) { doc.addPage(); y = margin; }
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(150, 90, 0);
            const label = el.getAttribute("data-testid") === "swing-marking" ? "SWING FEEL: PLAY 8TH NOTES AS A TRIPLET SHUFFLE" : (el.textContent || "").toUpperCase();
            doc.text(label, margin, y + 10);
            y += 16;
            continue;
          }
          const w = svg.width.baseVal.value;
          const h = svg.height.baseVal.value;
          const xml = new XMLSerializer().serializeToString(svg);
          const img = await new Promise((resolve, reject) => {
            const im = new Image();
            im.onload = () => resolve(im);
            im.onerror = reject;
            im.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
          });
          const c = document.createElement("canvas");
          c.width = Math.ceil(w * 2);
          c.height = Math.ceil(h * 2);
          const cx = c.getContext("2d");
          cx.fillStyle = "#fff";
          cx.fillRect(0, 0, c.width, c.height);
          cx.drawImage(img, 0, 0, c.width, c.height);
          const dw = Math.min(maxW, w * 0.75);
          const dh = (h * dw) / w;
          if (y + dh > pageH - margin) { doc.addPage(); y = margin; }
          doc.addImage(c.toDataURL("image/jpeg", 0.85), "JPEG", margin, y, dw, dh, undefined, "FAST");
          y += dh + 4;
        }
      } else {
        doc.setFont("courier", "normal");
        doc.setFontSize(9);
        doc.setTextColor(0);
        tabToAscii(tab).split("\n").forEach((ln) => {
          if (y > pageH - margin) { doc.addPage(); y = margin; }
          doc.text(ln, margin, y);
          y += 12;
        });
      }
      doc.save(`${(tab.title || "drum-tab").replace(/[^a-z0-9]/gi, "_")}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  const buildMix = () => ({
    mixer, solo: Array.from(solo), swing, ghostVol, accentVol,
    ...(fillSnap || {
      loopStart, loopEnd, trainer, trainerStep,
      trainerTarget: trainerTarget ? parseInt(trainerTarget, 10) : null,
    }),
  });
  const mixJson = JSON.stringify(buildMix());
  useEffect(() => {
    if (!savedId || !isPro) return undefined;
    const id = setTimeout(() => {
      if (lastMixRef.current === null) { lastMixRef.current = mixJson; return; }
      if (lastMixRef.current === mixJson) return;
      lastMixRef.current = mixJson;
      const { _libId, ...clean } = tab;
      api.put(`/tabs/${savedId}`, { tab: { ...clean, mix: JSON.parse(mixJson) } })
        .then(() => setMixSavedAt(Date.now()))
        .catch(() => toast.error("Could not save your mix"));
    }, 1000);
    return () => clearTimeout(id);
  }, [mixJson, savedId, isPro, tab]);
  useEffect(() => {
    if (!mixSavedAt) return undefined;
    const id = setTimeout(() => setMixSavedAt(0), 2500);
    return () => clearTimeout(id);
  }, [mixSavedAt]);

  const practiceFill = useCallback((a, b, name) => {
    stop();
    setFillSnap((cur) => cur || {
      loopStart, loopEnd, trainer, trainerStep,
      trainerTarget: trainerTarget ? parseInt(trainerTarget, 10) : null,
    });
    const full = tab.bpm || 120;
    const startBpm = Math.max(30, Math.round(full * 0.6));
    setLoopStart(a); setLoopEnd(b);
    loopStartRef.current = a; loopEndRef.current = b;
    loopLabelRef.current = `${name || "Fill"} (fill)`;
    setLoop(true); loopRef.current = true;
    seekRef.current = null; setSeekStep(null);
    setLoopCount(0); loopCountRef.current = 0;
    setHalfSpeed(false); halfSpeedRef.current = false;
    bpmRef.current = startBpm; setBpm(startBpm);
    setTrainerStep(5); trainerStepRef.current = 5;
    setTrainerTarget(String(full)); trainerTargetRef.current = full;
    trainerEveryRef.current = 4; trainerWrapRef.current = 0;
    setTrainer(true); trainerRef.current = true;
    setShowSticking(true);
    toast.success(`Fill practice: ${startBpm} BPM, +5 every 4 loops up to ${full} BPM`);
    setTimeout(() => playFromRef.current && playFromRef.current(), 80);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, stop, loopStart, loopEnd, trainer, trainerStep, trainerTarget]);

  const doSave = async (asFavorite = false) => {
    if (!user) {
      onRequireAuth();
      return null;
    }
    setSaving(true);
    try {
      let id = savedId;
      if (!id) {
        const mix = buildMix();
        lastMixRef.current = JSON.stringify(mix);
        const { _libId, ...clean } = tab;
        const { data } = await api.post("/tabs", { tab: { ...clean, mix } });
        id = data.id;
        setSavedId(id);
      }
      if (asFavorite) {
        const { data } = await api.patch(`/tabs/${id}/favorite`);
        setFavorite(data.is_favorite);
      }
      onSaved && onSaved();
      toast.success(asFavorite ? "Added to favorites" : "Saved to your library");
      return id;
    } catch (e) {
      toast.error("Could not save. Please try again.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const copyTab = async () => {
    try {
      await navigator.clipboard.writeText(tabToAscii(tab));
      toast.success("Tab copied as text");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const downloadMidi = () => {
    try {
      const blob = new Blob([tabToMidiBytes(tab)], { type: "audio/midi" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(tab.title || "drum-tab").replace(/[^a-z0-9]/gi, "_")}.mid`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("MIDI downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const printTab = () => {
    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const ascii = esc(tabToAscii(tab));
    const sheetHtml = viewMode === "sheet" ? document.querySelector("[data-testid=sheet-music]")?.innerHTML : null;
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Allow pop-ups to print");
      return;
    }
    const meta = [tab.bpm ? `${tab.bpm} BPM` : "", tab.time_signature, tab.difficulty,
      tab.key && tab.key !== "—" ? `Key: ${tab.key}` : ""].filter(Boolean).map(esc).join("  ·  ");
    w.document.write(
      `<html><head><title>${esc(tab.title)} — drum tab</title></head>` +
      `<body style="background:#fff;color:#000;padding:32px;font-family:Arial,Helvetica,sans-serif">` +
      `<h1 style="margin:0;font-size:22px">${esc(tab.title) || "Drum Tab"}</h1>` +
      `<div style="font-size:14px;color:#444;margin:2px 0">${esc(tab.artist)}</div>` +
      `<div style="font-size:12px;color:#666;margin-bottom:16px">${meta}</div>` +
      (sheetHtml
        ? `<div style="border-top:1px solid #ddd;padding-top:8px">${sheetHtml}</div><style>.sheet-playhead,.practice-fill-btn{display:none}[data-tab-row]{page-break-inside:avoid}</style>`
        : `<pre style="font-family:'JetBrains Mono',Menlo,monospace;font-size:12px;line-height:1.5;border-top:1px solid #ddd;padding-top:16px">${ascii}</pre>`) +
      (tab.notes ? `<p style="font-size:12px;color:#333;margin-top:16px"><strong>Tip:</strong> ${esc(tab.notes)}</p>` : "") +
      (teacherNote ? `<div style="font-size:13px;color:#222;margin-top:16px;border:1px solid #e0b040;border-radius:8px;padding:12px;background:#fff8e6"><strong>Teacher's tips${teacherNote.name ? ` — ${esc(teacherNote.name)}` : ""}:</strong><div style="white-space:pre-wrap;margin-top:6px">${esc(teacherNote.note)}</div></div>` : "") +
      `<p style="font-size:10px;color:#999;margin-top:24px">Generated with BeatFlow AI</p>` +
      `</body></html>`
    );
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/feedback/stats", { params: { title: tab.title, artist: tab.artist || "" } });
      setAccuracyStats(data);
    } catch (e) { setAccuracyStats(null); }
  }, [tab.title, tab.artist]);

  useEffect(() => {
    setVoted(null);
    setShowReasons(false);
    fetchStats();
  }, [fetchStats]);

  const vote = async (v, reason = "") => {
    setVoted(v);
    if (v === "down" && !reason) { setShowReasons(true); }
    try {
      await api.post("/feedback", { title: tab.title, artist: tab.artist, difficulty: tab.difficulty, vote: v, reason });
      if (v === "up" || reason) toast.success("Thanks for the feedback!");
      fetchStats();
    } catch (e) {
      /* ignore */
    }
  };

  const REASONS = [
    { key: "tempo too fast", label: "Too fast" },
    { key: "tempo too slow", label: "Too slow" },
    { key: "pattern is wrong for this song", label: "Wrong pattern" },
    { key: "groove is too simple, add more detail and fills", label: "Too simple" },
    { key: "groove is too busy, simplify it", label: "Too busy" },
    { key: "the feel/swing is off", label: "Wrong feel" },
  ];

  const regenerateWith = (reason) => {
    vote("down", reason);
    setShowReasons(false);
    if (onRegenerate) {
      toast.message("Re-transcribing with your feedback…");
      onRegenerate(`The previous transcription was not accurate: ${reason}. Please correct it.`);
    }
  };

  const createShareLink = async () => {
    const { data } = await api.post("/share", { tab, origin: window.location.origin, sticking: stickMap });
    return `${window.location.origin}/api/share/${data.id}/view`;
  };

  const captureSheet = async () => {
    const grab = () => Array.from(document.querySelectorAll("[data-testid=sheet-music] svg"));
    if (viewMode === "sheet") return grab();
    setViewMode("sheet");
    await new Promise((r) => setTimeout(r, 700));
    const svgs = grab().map((s) => s.cloneNode(true));
    setViewMode("grid");
    return svgs;
  };

  const setVol = (v, val) => setMixer((m) => ({ ...m, [v]: { ...m[v], vol: val } }));
  const toggleMute = (v) => setMixer((m) => ({ ...m, [v]: { ...m[v], mute: !m[v].mute } }));
  const toggleSolo = (v) =>
    setSolo((s) => {
      const n = new Set(s);
      n.has(v) ? n.delete(v) : n.add(v);
      return n;
    });

  const cellClickRef = useRef(() => {});
  const onSheetSeek = useCallback((g) => cellClickRef.current(g), []);
  const hasTripletFeel = stepsPerBeat === 3;
  const hasGhosts = useMemo(() => flat.some((evs) => evs.some((e) => e.ghost)), [flat]);
  const hasAccents = useMemo(() => flat.some((evs) => evs.some((e) => e.accent && !e.open)), [flat]);
  const tsDen = parseInt(String(tab.time_signature || "4/4").split("/")[1], 10) || 4;
  cellClickRef.current = (g) => (loopMarkMode ? setMarker(g) : seekTo(g));
  // Memoised so per-second practice-timer updates don't redraw thousands of cells.
  const gridEl = useMemo(() => (
        <div ref={scrollRef} className="space-y-6 overflow-x-auto scroll-thin" data-testid="tab-grid">
          {sections.map((sec, si) => {
            const z = ZOOMS[zoomIdx];
            const labelW = Math.round(40 * Math.min(z, 1.5));
            const usable = Math.max(0, gridWidth - labelW - 4);
            const usable0 = Math.max(0, gridWidth - 44);
            const perRow1 = Math.max(1, Math.floor(usable0 / (spm * 18)) || 1);
            const base = Math.max(14, Math.min(26, Math.floor(usable0 / (spm * perRow1)) || 20));
            const want = base * z;
            const rows = [];
            let cellPx;
            if (want * spm <= usable) {
              const perRow = Math.max(1, Math.floor(usable / (spm * want)) || 1);
              cellPx = z === 1 ? Math.floor(usable / (spm * perRow)) : Math.floor(want);
              cellPx = Math.max(14, Math.min(z === 1 ? 26 : Math.round(want), cellPx));
              for (let r = 0; r < sec.measures.length; r += perRow) {
                rows.push(sec.measures.slice(r, r + perRow).map((m) => ({ m, from: 0, len: m.steps })));
              }
            } else {
              const fitSteps = Math.max(1, Math.floor(usable / want));
              let seg = stepsPerBeat;
              for (let d = spm - 1; d >= stepsPerBeat; d--) {
                if (spm % d === 0 && d % stepsPerBeat === 0 && d <= fitSteps) { seg = d; break; }
              }
              cellPx = Math.max(14, Math.floor(Math.min(want, usable / seg)));
              sec.measures.forEach((m) => {
                const mseg = Math.max(1, Math.round((seg * m.steps) / spm));
                for (let f = 0; f < m.steps; f += mseg) rows.push([{ m, from: f, len: Math.min(mseg, m.steps - f) }]);
              });
            }
            const dotBig = Math.min(Math.round(13 * z), cellPx - 4);
            const dotSmall = Math.min(Math.round(9 * z), cellPx - 6);
            return (
            <div key={si}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-heading text-sm uppercase tracking-widest text-[#ffab00]">
                  {sec.name}
                </span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              {rows.map((rowMeasures, ri) => (
              <div key={ri} className="inline-block min-w-full mb-3" data-tab-row>
                {usedVoices.map((v) => (
                  <div key={v} className="flex items-center mb-1">
                    <div className="shrink-0 font-tab font-bold" style={{ color: VOICE_COLORS[v], width: labelW, fontSize: Math.round(12 * Math.min(z, 1.6)) }}>
                      {v}
                    </div>
                    <div className="flex">
                      {rowMeasures.map(({ m, from, len }, mi) => (
                        <div
                          key={mi}
                          className="flex"
                          style={{ boxShadow: mi !== 0 ? "inset 2px 0 0 rgba(255,255,255,0.15)" : "none" }}
                        >
                          {Array.from({ length: len }).map((_, k) => {
                            const i = from + k;
                            const cell = parseCell((m.lanes[v] || "")[i]);
                            const global = m.startStep + i;
                            const beatStart = i % Math.max(1, Math.round(m.steps / beats)) === 0;
                            const cw = m.steps === spm ? cellPx : (cellPx * spm) / m.steps;
                            const inLoop = loopStart != null && loopEnd != null && global >= loopStart && global <= loopEnd;
                            const isMarker = global === loopStart || global === loopEnd;
                            return (
                              <div
                                key={i}
                                data-step={global}
                                onClick={() => cellClickRef.current(global)}
                                data-testid={v === usedVoices[0] ? `seek-step-${global}` : undefined}
                                title={loopMarkMode ? "Set loop marker here" : "Play along from here"}
                                className={`tab-cell flex items-center justify-center cursor-pointer${global === activeStepRef.current ? " is-active" : ""}`}
                                style={{
                                  width: cw,
                                  height: cellPx,
                                  boxShadow: beatStart && k !== 0 ? "inset 1px 0 0 rgba(255,255,255,0.08)" : "none",
                                  background: isMarker
                                    ? "rgba(34,197,94,0.4)"
                                    : global === seekStep
                                    ? "rgba(255,171,0,0.16)"
                                    : inLoop
                                    ? "rgba(34,197,94,0.1)"
                                    : "transparent",
                                }}
                              >
                                {cell.ghost ? (
                                  <span className="tab-dot font-tab leading-none" style={{ fontSize: Math.max(9, dotBig * 1.1), color: VOICE_COLORS[v], opacity: 0.75 }} title="Ghost note — play very softly" data-testid={`ghost-cell-${global}`}>g</span>
                                ) : cell.hit ? (
                                  <span
                                    className="tab-dot block rounded-full"
                                    style={{
                                      width: cell.accent ? dotBig : dotSmall,
                                      height: cell.accent ? dotBig : dotSmall,
                                      background: VOICE_COLORS[v],
                                      color: VOICE_COLORS[v],
                                      opacity: cell.accent ? 1 : 0.8,
                                    }}
                                  />
                                ) : (
                                  <span className="block w-1 h-1 rounded-full bg-white/10" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              ))}
            </div>
            );
          })}
        </div>
  ), [sections, usedVoices, gridWidth, zoomIdx, spm, stepsPerBeat, loopStart, loopEnd, seekStep, loopMarkMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Space bar / Bluetooth page-turner pedal: play-pause; "back" pedal restarts.
  const keyActionsRef = useRef({});
  keyActionsRef.current = {
    toggle: () => { if (!playing && fullScreen) forceCountRef.current = true; togglePlay(); },
    restart,
  };
  useEffect(() => {
    const onKey = (e) => {
      if (tapSyncOpen || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.closest?.("[role=dialog],[role=menu],[role=listbox],[role=slider],[role=radiogroup],[role=tablist]"))) return;
      if (["Space", "PageDown", "ArrowDown", "ArrowRight", "Enter", "MediaPlayPause"].includes(e.code) || e.key === "MediaPlayPause") {
        e.preventDefault();
        if (t && t.tagName === "BUTTON") t.blur();
        keyActionsRef.current.toggle();
      } else if (["PageUp", "ArrowUp", "ArrowLeft"].includes(e.code)) {
        e.preventDefault();
        keyActionsRef.current.restart();
      }
    };
    // A focused button would otherwise "click" itself on Space release.
    const onKeyUp = (e) => {
      if (tapSyncOpen || e.code !== "Space") return;
      const t = e.target;
      if (t && t.tagName === "BUTTON" && !t.closest?.("[role=dialog],[role=menu],[role=listbox]")) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("keyup", onKeyUp); };
  }, [tapSyncOpen]);

  return (
    <div className="relative rounded-2xl bg-[#12161f] border border-white/10 overflow-clip" data-testid="drum-tab-viewer">
      {countNum > 0 && fullScreen && (
        <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-[#0b0c10]/80 backdrop-blur-sm pointer-events-none" data-testid="fullscreen-count-in">
          <span key={countNum} className="font-heading text-[10rem] sm:text-[16rem] leading-none text-[#00f0ff] animate-in zoom-in-50 fade-in duration-100">
            {countNum}
          </span>
          <div className="flex gap-4 sm:gap-6 mt-6">
            {Array.from({ length: beats }).map((_, i) => (
              <span
                key={i}
                className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full transition-colors duration-75 ${i < countNum ? (i === 0 ? "bg-[#ffab00] shadow-[0_0_30px_#ffab00]" : "bg-[#00f0ff] shadow-[0_0_30px_#00f0ff]") : "bg-white/10"}`}
                data-testid={`count-light-${i + 1}`}
              />
            ))}
          </div>
        </div>
      )}
      <TapSync open={tapSyncOpen} onOpenChange={setTapSyncOpen} onResult={setSyncExact} />
      {countNum > 0 && !fullScreen && (
        <div
          className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-[#0b0c10]/75 backdrop-blur-sm pointer-events-none"
          data-testid="count-in-overlay"
        >
          <span key={countNum} className="font-heading text-8xl text-[#00f0ff] animate-in zoom-in-50 fade-in duration-150">
            {countNum}
          </span>
          <span className="text-xs font-tab uppercase tracking-[0.3em] text-slate-400 mt-2">Count in</span>
        </div>
      )}
      {goalFlash && (
        <div
          className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-[#A855F7]/20 backdrop-blur-sm pointer-events-none animate-in fade-in duration-150"
          data-testid="goal-flash"
        >
          <Trophy className="w-16 h-16 text-[#ffab00] animate-in zoom-in-50 duration-200" />
          <span className="font-heading text-4xl text-[#ffab00] mt-3 uppercase tracking-tight">Goal!</span>
        </div>
      )}
      {/* Header */}
      <div className="p-5 sm:p-6 border-b border-white/10 bg-gradient-to-r from-[#00f0ff]/5 to-transparent">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[#00f0ff] mb-1">
              <Music2 className="w-4 h-4" />
              <span className="text-xs font-tab uppercase tracking-widest">Drum Transcription</span>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight leading-none">
              {tab.title}
            </h2>
            <p className="text-slate-400 text-sm mt-1">{tab.artist}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="icon"
              onClick={printTab}
              className="border-white/15 bg-white/5"
              data-testid="print-tab-button"
              title="Print this tab"
            >
              <Printer className="w-4 h-4" />
            </Button>
            {isPro ? (
              <>
                <Button
                  variant="outline" size="icon"
                  onClick={() => doSave(true)} disabled={saving}
                  className={`border-white/15 ${favorite ? "bg-[#ffab00]/15 text-[#ffab00] border-[#ffab00]/40" : "bg-white/5"}`}
                  data-testid="favorite-toggle-button"
                >
                  <Star className="w-4 h-4" fill={favorite ? "#ffab00" : "none"} />
                </Button>
                <Button
                  variant="outline" size="icon"
                  onClick={() => doSave(false)} disabled={saving || !!savedId}
                  className="border-white/15 bg-white/5" data-testid="save-tab-button"
                >
                  <Save className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline" size="icon"
                  onClick={copyTab}
                  className="border-white/15 bg-white/5" data-testid="copy-tab-button"
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="border-white/15 bg-white/5" data-testid="export-menu-button">
                      <Download className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#12161f] border-white/10">
                    <DropdownMenuItem onClick={downloadMidi} className="cursor-pointer focus:bg-white/5" data-testid="download-midi-button">
                      <Download className="w-4 h-4 mr-2" /> Download MIDI
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadPdf} className="cursor-pointer focus:bg-white/5" data-testid="download-pdf-button">
                      <FileText className="w-4 h-4 mr-2" /> Download PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                onClick={onUpgrade}
                className="bg-[#ffab00] text-black hover:bg-[#ffc233] font-semibold"
                data-testid="viewer-upgrade-button"
              >
                <Crown className="w-4 h-4 mr-2" /> Upgrade to save & export
              </Button>
            )}
            <ShareSocial tab={tab} isPro={isPro} onUpgrade={onUpgrade} createLink={createShareLink} captureSheet={captureSheet} onMakeClip={() => { stop(); setClipOpen(true); }} />
            {clipOpen && <ClipMaker tab={{ ...tab, mix: buildMix() }} spm={spm} beats={beats} den={tsDen} onClose={() => setClipOpen(false)} />}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Badge className="bg-[#00f0ff]/15 text-[#00f0ff] border-0 font-tab">{bpm} BPM</Badge>
          {halfSpeed && (
            <Badge className="bg-[#00f0ff]/15 text-[#00f0ff] border-0 font-tab" data-testid="half-speed-badge">½× speed</Badge>
          )}
          <Badge className="bg-white/5 text-slate-300 border-0 font-tab">{tab.time_signature}</Badge>
          <Badge className="bg-white/5 text-slate-300 border-0 font-tab">{tab.difficulty}</Badge>
          {tab.key && tab.key !== "—" && (
            <Badge className="bg-white/5 text-slate-300 border-0 font-tab">Key: {tab.key}</Badge>
          )}
        </div>
      </div>

      {/* Floating stop button so the player can be stopped while the page follows the music */}
      {playing && !fullScreen && (
        <button
          onClick={togglePlay}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-5 z-50 h-12 w-12 rounded-full bg-[#FF3366]/90 hover:bg-[#ff4d7a] text-white shadow-lg flex items-center justify-center"
          title="Pause"
          data-testid="floating-pause-button"
        >
          <Pause className="w-6 h-6" />
        </button>
      )}
      {/* Transport */}
      <div className="p-4 sm:p-5 border-b border-white/10 flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={togglePlay}
            className={`h-11 w-11 rounded-full p-0 ${playing ? "bg-[#FF3366] hover:bg-[#ff4d7a] text-white" : "bg-[#00f0ff] hover:bg-[#33f4ff] text-black glow-cyan"}`}
            data-testid="play-pause-button"
          >
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </Button>
          <Button variant="outline" size="icon" onClick={restart}
            className="h-11 w-11 rounded-full border-white/15 bg-white/5" data-testid="restart-button">
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button
            variant="outline" size="icon" onClick={() => setLoop((l) => !l)}
            className={`h-11 w-11 rounded-full border-white/15 ${loop ? "bg-[#00f0ff]/15 text-[#00f0ff] border-[#00f0ff]/40" : "bg-white/5"}`}
            data-testid="loop-button"
          >
            <Repeat className="w-4 h-4" />
          </Button>
          <Select value={String(loopSection)} onValueChange={(v) => setLoopSection(v === "all" ? "all" : parseInt(v, 10))}>
            <SelectTrigger className="h-9 w-auto gap-2 bg-white/5 border-white/15 text-xs font-tab" data-testid="loop-section-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#12161f] border-white/10">
              <SelectItem value="all">Whole song</SelectItem>
              {sections.map((s, i) => (
                <SelectItem key={i} value={String(i)} data-testid={`loop-section-${i}`}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="sm" onClick={toggleLoopMark}
            className={`h-9 border-white/15 ${
              loopStart != null && loopEnd != null
                ? "bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/40"
                : loopMarkMode
                ? "bg-[#ffab00]/15 text-[#ffab00] border-[#ffab00]/40"
                : "bg-white/5 text-slate-300"
            }`}
            data-testid="loop-ab-button"
          >
            <Flag className="w-4 h-4 mr-1.5" />
            {loopStart != null && loopEnd != null ? "A–B on" : loopMarkMode ? "Pick beats…" : "A–B loop"}
          </Button>
          {loopStart != null && loopEnd != null && loopCount > 0 && (
            <Badge className="bg-[#22c55e]/15 text-[#22c55e] border-0 font-tab" data-testid="loop-count-badge">
              ×{loopCount}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-3 flex-1 min-w-[180px]">
          <span className="text-xs text-slate-500 font-tab w-10">{bpm}</span>
          <Slider
            value={[bpm]} min={40} max={240} step={1}
            onValueChange={(v) => setBpm(v[0])}
            className="flex-1" data-testid="tempo-bpm-slider"
          />
        </div>

        <div className="flex items-center gap-2 min-w-[130px]">
          <Volume2 className="w-4 h-4 text-slate-500" />
          <Slider
            value={[volume]} min={0} max={100} step={1}
            onValueChange={(v) => setVolume(v[0])}
            className="w-24" data-testid="volume-slider"
          />
        </div>

        <button
          onClick={() => setCountIn((c) => !c)}
          data-testid="count-in-toggle"
          className={`text-xs font-tab px-3 py-2 rounded-lg border transition-colors ${countIn ? "border-[#ffab00]/40 text-[#ffab00] bg-[#ffab00]/10" : "border-white/10 text-slate-400 bg-white/5"}`}
        >
          Count-in
        </button>
        <Button
          variant="outline" size="sm" onClick={() => (isPro ? setShowMixer((s) => !s) : onUpgrade())}
          className={`h-9 border-white/15 ${showMixer ? "bg-[#00f0ff]/15 text-[#00f0ff] border-[#00f0ff]/40" : "bg-white/5 text-slate-300"}`}
          data-testid="mixer-toggle-button"
        >
          <SlidersHorizontal className="w-4 h-4 mr-2" /> Mixer
        </Button>
        {isPro && (
          <div className="flex items-center gap-2 min-w-[150px]" data-testid="swing-control">
            <Waves className="w-4 h-4 text-slate-500" />
            <span className="text-[10px] text-slate-500 font-tab w-9">{swing}%</span>
            <Slider
              value={[swing]} min={0} max={100} step={5}
              onValueChange={(v) => setSwing(v[0])}
              className="w-24" data-testid="swing-slider"
            />
          </div>
        )}
        {hasAccents && (
          <div className="flex items-center gap-2 min-w-[170px]" title="How loud accented notes play" data-testid="accent-volume-control">
            <span className="text-[10px] text-slate-400 font-tab uppercase tracking-widest">Accent</span>
            <span className="text-[10px] text-slate-400 font-tab w-9" data-testid="accent-volume-value">{accentVol}%</span>
            <Slider value={[accentVol]} min={0} max={200} step={10} onValueChange={(v) => setAccentVol(v[0])} className="w-24" data-testid="accent-volume-slider" />
          </div>
        )}
        {mixSavedAt > 0 && <span className="fixed bottom-5 left-5 z-50 rounded-full border border-emerald-400/40 bg-[#0e121a]/95 px-3 py-1.5 text-[10px] font-tab uppercase tracking-widest text-emerald-400 shadow-lg" data-testid="mix-saved-note">Mix saved</span>}
        {hasGhosts && (
          <div className="flex items-center gap-2 min-w-[170px]" title="How loud ghost notes play" data-testid="ghost-volume-control">
            <span className="text-[10px] text-slate-400 font-tab uppercase tracking-widest">Ghost</span>
            <span className="text-[10px] text-slate-400 font-tab w-9" data-testid="ghost-volume-value">{ghostVol}%</span>
            <Slider value={[ghostVol]} min={0} max={100} step={5} onValueChange={(v) => setGhostVol(v[0])} className="w-24" data-testid="ghost-volume-slider" />
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline" size="sm" onClick={toggleMetroOnly}
            className={`h-9 border-white/15 ${metroOnly ? "bg-[#ffab00]/15 text-[#ffab00] border-[#ffab00]/40" : "bg-white/5 text-slate-300"}`}
            data-testid="metronome-only-button"
          >
            <Timer className="w-4 h-4 mr-1.5" /> Click only
          </Button>
          <Select value={accentMode} onValueChange={setAccentMode}>
            <SelectTrigger className="h-9 w-auto gap-1 bg-white/5 border-white/15 text-xs font-tab" data-testid="accent-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#12161f] border-white/10">
              <SelectItem value="downbeat">Accent: beat 1</SelectItem>
              <SelectItem value="all">Accent: every beat</SelectItem>
              <SelectItem value="alternate">Accent: every other</SelectItem>
              <SelectItem value="none">Accent: none</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="sm" onClick={toggleHalfSpeed}
            className={`h-9 border-white/15 ${halfSpeed ? "bg-[#00f0ff]/15 text-[#00f0ff] border-[#00f0ff]/40" : "bg-white/5 text-slate-300"}`}
            data-testid="half-speed-button"
          >
            <Rewind className="w-4 h-4 mr-1.5" /> ½× Speed
          </Button>
          <Button
            variant="outline" size="sm" onClick={toggleTrainer}
            className={`h-9 border-white/15 ${trainer ? "bg-[#A855F7]/15 text-[#A855F7] border-[#A855F7]/40" : "bg-white/5 text-slate-300"}`}
            data-testid="trainer-toggle-button"
          >
            <Gauge className="w-4 h-4 mr-1.5" /> Trainer
          </Button>
          {trainer && (
            <>
              <Select value={String(trainerStep)} onValueChange={(v) => setTrainerStep(parseInt(v, 10))}>
                <SelectTrigger className="h-9 w-auto gap-1 bg-white/5 border-white/15 text-xs font-tab" data-testid="trainer-step-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#12161f] border-white/10">
                  <SelectItem value="2">+2 BPM / {fillSnap ? "4 loops" : "loop"}</SelectItem>
                  <SelectItem value="5">+5 BPM / {fillSnap ? "4 loops" : "loop"}</SelectItem>
                  <SelectItem value="10">+10 BPM / {fillSnap ? "4 loops" : "loop"}</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1.5">
                <Target className="w-4 h-4 text-[#A855F7]" />
                <Input
                  type="number" min="40" max="240"
                  value={trainerTarget}
                  onChange={(e) => setTrainerTarget(e.target.value)}
                  placeholder="Goal BPM"
                  className="h-9 w-24 bg-white/5 border-white/15 text-xs font-tab"
                  data-testid="trainer-target-input"
                />
              </div>
            </>
          )}
          <div className="flex items-center gap-1.5 text-xs font-tab text-slate-400 ml-auto" data-testid="practice-timer">
            <Clock className="w-4 h-4 text-[#ffab00]" />
            <span>{fmtTime(practiceSecs)}</span>
            <span className="text-slate-600 hidden sm:inline">practiced today</span>
          </div>
        </div>
      </div>

      {/* Weekly practice goal */}
      <div className="px-4 sm:px-5 py-2.5 border-b border-white/10 flex items-center gap-3 flex-wrap bg-white/[0.02]" data-testid="weekly-practice">
        <Trophy className="w-4 h-4 text-[#ffab00] shrink-0" />
        <span className="text-[11px] font-tab uppercase tracking-widest text-slate-500 shrink-0">Weekly goal</span>
        <Input
          type="number" min="0"
          value={weekGoal}
          onChange={(e) => saveGoal(e.target.value)}
          placeholder="min"
          className="h-8 w-20 bg-white/5 border-white/15 text-xs font-tab shrink-0"
          data-testid="week-goal-input"
        />
        <div className="flex-1 min-w-[120px]">
          <Progress value={weekPct} className="h-2" data-testid="week-progress" />
        </div>
        <span className="text-xs font-tab text-slate-400 shrink-0" data-testid="week-progress-text">
          {weekMin} / {weekGoalNum || "—"} min this week
        </span>
        {streakCount > 0 && (
          <Badge className="bg-[#ffab00]/15 text-[#ffab00] border-0 font-tab shrink-0" data-testid="streak-badge">
            <Flame className="w-3 h-3 mr-1" />{streakCount}w
          </Badge>
        )}
        <Button size="sm" variant="outline" onClick={openSummary} className="h-8 border-white/15 bg-white/5 text-xs shrink-0" data-testid="session-summary-button">
          <ClipboardList className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Session</span>
        </Button>
        <Button size="sm" variant="outline" onClick={shareProgress} className="h-8 border-white/15 bg-white/5 text-xs shrink-0" data-testid="share-progress-button">
          <Share2 className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Share</span>
        </Button>
      </div>

      {showMixer && (
        <div className="p-4 sm:p-5 border-b border-white/10 bg-white/[0.02]" data-testid="mixer-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {usedVoices.map((v) => {
              const m = mixer[v] || { vol: 1, mute: false };
              const soloed = solo.has(v);
              return (
                <div key={v} className="flex items-center gap-2.5">
                  <span className="w-9 font-tab text-xs font-bold" style={{ color: VOICE_COLORS[v] }}>{v}</span>
                  <button
                    onClick={() => toggleMute(v)} data-testid={`mix-mute-${v}`}
                    className={`w-7 h-7 rounded text-[11px] font-bold border transition-colors ${m.mute ? "bg-[#FF3366]/20 text-[#FF3366] border-[#FF3366]/40" : "border-white/15 text-slate-400"}`}
                  >M</button>
                  <button
                    onClick={() => toggleSolo(v)} data-testid={`mix-solo-${v}`}
                    className={`w-7 h-7 rounded text-[11px] font-bold border transition-colors ${soloed ? "bg-[#ffab00]/20 text-[#ffab00] border-[#ffab00]/40" : "border-white/15 text-slate-400"}`}
                  >S</button>
                  <Slider
                    value={[Math.round(m.vol * 100)]} min={0} max={100} step={1}
                    onValueChange={(val) => setVol(v, val[0] / 100)}
                    className="flex-1" data-testid={`mix-volume-${v}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sequencer grid */}
      <div
        ref={gridSectionRef}
        className={fullScreen ? "fixed inset-0 z-[70] overflow-y-auto bg-[#0b0c10] px-4 sm:px-8 pb-8" : "p-4 sm:p-5 bg-[#0d1117]"}
        data-testid={fullScreen ? "fullscreen-tab" : "tab-section"}
      >
        <div
          className={fullScreen ? "sticky top-0 z-10 -mx-4 sm:-mx-8 mb-4 px-4 sm:px-8 py-3 bg-[#0b0c10]/95 backdrop-blur border-b border-white/10" : ""}
          data-fs-bar={fullScreen ? "true" : undefined}
        >
        {fullScreen && (
          <div className="flex items-center gap-3 mb-2">
            <Button
              onClick={() => { if (!playing) forceCountRef.current = true; togglePlay(); }}
              className={`h-11 w-11 rounded-full p-0 shrink-0 ${playing ? "bg-[#FF3366] hover:bg-[#ff4d7a] text-white" : "bg-[#00f0ff] hover:bg-[#33f4ff] text-black"}`}
              data-testid="fullscreen-play-button"
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </Button>
            <div className="min-w-0">
              <div className="font-heading text-lg uppercase tracking-tight truncate">{tab.title}</div>
              <div className="text-xs text-slate-500 font-tab">{tab.artist ? `${tab.artist} · ` : ""}{bpm} BPM</div>
            </div>
          </div>
        )}
        <div className={`flex items-center justify-between gap-3 flex-wrap ${fullScreen ? "" : "mb-3"}`}>
          <p className={`text-[11px] text-slate-500 font-tab items-center gap-1.5 ${fullScreen ? "hidden" : "flex"}`} data-testid="playalong-hint">
            <MousePointerClick className="w-3.5 h-3.5 text-[#ffab00]" />
            {loopMarkMode
              ? loopStart == null
                ? "Click the beat where your loop should START"
                : "Now click the beat where your loop should END"
              : "Click any beat to play along from there — or use A–B loop to drill a custom range"}
            <span className="hidden sm:inline text-slate-600"> · Space bar or page-turner pedal: play / pause</span>
          </p>
          <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center rounded-full border border-white/15 overflow-hidden" data-testid="sync-controls" title="Is the moving highlight behind or ahead of what you hear? Nudge it until they match.">
            <button onClick={() => changeSync(-25)} className="h-7 px-2 flex items-center gap-1 text-[10px] font-tab text-slate-300 hover:text-[#00f0ff]" title="Highlight is ahead of the sound — move it later" data-testid="sync-later-button">
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => changeSync(0)} className="px-1.5 text-[10px] font-tab text-slate-400 whitespace-nowrap hover:text-[#ffab00]" title="Reset sync" data-testid="sync-value">
              Sync {syncMs > 0 ? `+${syncMs}` : syncMs}ms
            </button>
            <button onClick={() => changeSync(25)} className="h-7 px-2 flex items-center gap-1 text-[10px] font-tab text-slate-300 hover:text-[#00f0ff]" title="Highlight is behind the sound — move it earlier" data-testid="sync-earlier-button">
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => { if (playing) stop(); setTapSyncOpen(true); }} className="h-7 px-2.5 border-l border-white/15 text-[10px] font-tab uppercase tracking-widest text-[#ffab00] hover:bg-[#ffab00]/10" title="Tap along to 4 clicks to set Sync automatically" data-testid="tap-sync-open">
              Auto
            </button>
          </div>
          <div className="inline-flex items-center rounded-full border border-white/15 overflow-hidden" data-testid="zoom-controls">
            <button onClick={() => changeZoom(-1)} disabled={zoomIdx === 0} className="h-7 w-8 flex items-center justify-center text-slate-300 hover:text-[#00f0ff] disabled:opacity-30" title="Smaller notation" data-testid="zoom-out-button">
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <span className="px-1.5 text-[10px] font-tab text-slate-400 w-10 text-center" data-testid="zoom-level">{Math.round(ZOOMS[zoomIdx] * 100)}%</span>
            <button onClick={() => changeZoom(1)} disabled={zoomIdx === ZOOMS.length - 1} className="h-7 w-8 flex items-center justify-center text-slate-300 hover:text-[#00f0ff] disabled:opacity-30" title="Bigger notation — easier to read from behind the kit" data-testid="zoom-in-button">
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="inline-flex rounded-full border border-white/15 overflow-hidden text-[10px] font-tab uppercase tracking-widest" data-testid="view-mode-toggle">
            {[["grid", "Grid"], ["sheet", "Sheet music"]].map(([v, l]) => (
              <button key={v} onClick={() => changeView(v)} className={`h-7 px-3 transition-colors ${viewMode === v ? "bg-[#00f0ff]/15 text-[#00f0ff]" : "text-slate-400 hover:text-slate-200"}`} data-testid={`view-mode-${v}`}>
                {l}
              </button>
            ))}
          </div>
          {viewMode === "sheet" && (
            <>
              {hasTripletFeel && (
                <button onClick={toggleSwingNotation} className={`h-7 px-3 rounded-full border text-[10px] font-tab uppercase tracking-widest transition-colors ${swingNotation ? "border-[#ffab00]/50 text-[#ffab00] bg-[#ffab00]/10" : "border-white/15 text-slate-400"}`} title="Write shuffles as straight 8ths with a swing marking, like printed charts" data-testid="swing-notation-toggle">
                  Swing 8ths: {swingNotation ? "On" : "Off"}
                </button>
              )}
              <button onClick={toggleSticking} className={`h-7 px-3 rounded-full border text-[10px] font-tab uppercase tracking-widest transition-colors ${showSticking ? "border-[#ffab00]/50 text-[#ffab00] bg-[#ffab00]/10" : "border-white/15 text-slate-400"}`} title="Show R/L sticking under fills" data-testid="sticking-toggle">
                Sticking: {showSticking ? "On" : "Off"}
              </button>
              <button onClick={() => setEditSticking((v) => !v)} className={`h-7 px-3 rounded-full border text-[10px] font-tab uppercase tracking-widest transition-colors ${editSticking ? "border-[#3b82f6]/60 text-[#60a5fa] bg-[#3b82f6]/15" : "border-white/15 text-slate-400 hover:text-slate-200"}`} title="Click notes to set your own R / L sticking (saved to your account)" data-testid="edit-sticking-toggle">
                {editSticking ? "Done editing" : "Edit sticking"}
              </button>
              <div className="inline-flex rounded-full border border-white/15 overflow-hidden text-[10px] font-tab uppercase tracking-widest" data-testid="sheet-layout-toggle">
                {[[false, "Full"], [true, "Compact"]].map(([v, l]) => (
                  <button key={l} onClick={() => compactSheet !== v && toggleCompact()} className={`h-7 px-3 transition-colors ${compactSheet === v ? "bg-[#ffab00]/15 text-[#ffab00]" : "text-slate-400 hover:text-slate-200"}`} title={v ? "Show each section once with repeat signs, like a printed chart" : "Write out every bar"} data-testid={`sheet-layout-${l.toLowerCase()}`}>
                    {l}
                  </button>
                ))}
              </div>
            </>
          )}
          <button
            onClick={toggleFullScreen}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-tab uppercase tracking-widest transition-colors ${
              fullScreen ? "bg-[#ffab00]/15 text-[#ffab00] border border-[#ffab00]/40" : "border border-white/15 text-slate-300 hover:text-[#ffab00]"
            }`}
            title={fullScreen ? "Exit full screen (Esc)" : "Full screen: show only the tab, for reading behind the kit"}
            data-testid="fullscreen-toggle"
          >
            {fullScreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
            {fullScreen ? "Exit" : "Full screen"}
          </button>
          <button
            onClick={() => setFollowPlayhead((f) => !f)}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-tab uppercase tracking-widest transition-colors ${
              followPlayhead
                ? "bg-[#00f0ff]/15 text-[#00f0ff] border border-[#00f0ff]/40"
                : "border border-white/15 text-slate-400 hover:text-slate-200"
            }`}
            title="When on, the tab scrolls to follow the beat. Turn off to keep it still."
            data-testid="follow-playhead-toggle"
          >
            <MousePointerClick className="w-3 h-3" />
            Follow: {followPlayhead ? "On" : "Off"}
          </button>
          </div>
        </div>
        </div>
        <div ref={widthRef}>
          {viewMode === "sheet" ? (
            <div className="rounded-xl bg-[#f7f4ec] text-[#1a1a1a] px-3 pb-3">
              {editSticking && (
                <div className="flex flex-wrap items-center gap-3 pt-3 text-xs text-[#1a1a1a]" data-testid="edit-sticking-banner">
                  <span><strong>Edit sticking:</strong> click any note to cycle R → L → blank. {user ? "Saved to your account automatically." : "Sign in to save."}{stickSource === "teacher" ? " Showing your teacher's sticking — edits save as your own copy." : stickSource === "shared" ? " Showing the sharer's sticking — edits save as your own copy." : ""}</span>
                  {user && Object.keys(stickMap).length > 0 && (
                    <button onClick={resetSticking} className="underline text-[#0b5cad]" data-testid="reset-sticking-button">Reset to suggested</button>
                  )}
                </div>
              )}
              <SheetMusic sections={sections} spm={spm} beats={beats} den={tsDen} swing={swingNotation && hasTripletFeel} sticking={showSticking} compact={compactSheet} onPracticeFill={practiceFill} stickMap={stickMap} editSticking={editSticking} onStickEdit={setStick} width={gridWidth ? gridWidth - 24 : 0} zoom={ZOOMS[zoomIdx]} apiRef={sheetApiRef} onSeek={onSheetSeek} />
            </div>
          ) : gridEl}
          {viewMode === "sheet" && <NotationKey width={gridWidth ? gridWidth - 56 : 0} />}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-5 pt-4 border-t border-white/10">
          {usedVoices.map((v) => (
            <div key={v} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: VOICE_COLORS[v] }} />
              <span className="text-[11px] text-slate-500 font-tab">{v} · {VOICE_LABELS[v]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="px-5 py-4 border-t border-white/10 flex flex-col gap-3" data-testid="feedback-bar">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-slate-500 font-tab uppercase tracking-widest">Groove feel right?</span>
          <Button
            size="sm" variant="outline" onClick={() => vote("up")}
            className={`h-8 border-white/15 ${voted === "up" ? "text-[#00f0ff] border-[#00f0ff]/40 bg-[#00f0ff]/10" : "text-slate-400 bg-white/5"}`}
            data-testid="feedback-up-button"
          >
            <ThumbsUp className="w-4 h-4" />
          </Button>
          <Button
            size="sm" variant="outline" onClick={() => vote("down")}
            className={`h-8 border-white/15 ${voted === "down" ? "text-[#FF3366] border-[#FF3366]/40 bg-[#FF3366]/10" : "text-slate-400 bg-white/5"}`}
            data-testid="feedback-down-button"
          >
            <ThumbsDown className="w-4 h-4" />
          </Button>
          {accuracyStats && accuracyStats.total > 0 && (
            <span className="text-xs font-tab text-slate-400 flex items-center gap-1" data-testid="accuracy-badge">
              <span className={accuracyStats.accuracy >= 60 ? "text-[#00f0ff]" : "text-[#ffab00]"}>{accuracyStats.accuracy}% accurate</span>
              <span className="text-slate-600">· {accuracyStats.total} vote{accuracyStats.total !== 1 ? "s" : ""}</span>
            </span>
          )}
          {voted === "up" && <span className="text-xs text-slate-500">Thanks for helping us tune the AI.</span>}
          {onRegenerate && !showReasons && (
            <button
              onClick={() => setShowReasons(true)}
              className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-tab border border-[#ffab00]/40 text-[#ffab00] bg-[#ffab00]/5 hover:bg-[#ffab00]/15 transition-colors"
              data-testid="not-accurate-regenerate-button"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Not accurate? Regenerate
            </button>
          )}
        </div>
        {showReasons && (
          <div className="flex flex-col gap-2" data-testid="feedback-reasons">
            <span className="text-xs text-slate-500 font-tab">What was off? Tap one to fix it:</span>
            <div className="flex flex-wrap gap-2">
              {REASONS.map((r) => (
                <button
                  key={r.key}
                  onClick={() => regenerateWith(r.key)}
                  className="h-7 px-3 rounded-full text-xs font-tab bg-white/5 border border-white/15 text-slate-300 hover:border-[#00f0ff]/40 hover:text-[#00f0ff] transition-colors"
                  data-testid={`feedback-reason-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {onRegenerate && (
              <form
                onSubmit={(e) => { e.preventDefault(); if (customReason.trim().length > 2) { regenerateWith(customReason.trim()); setCustomReason(""); } }}
                className="flex gap-2 max-w-xl"
              >
                <Input
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  maxLength={300}
                  placeholder="Or describe it, e.g. intro starts with toms only, no hi-hat"
                  className="h-8 text-xs bg-white/5 border-white/15"
                  data-testid="custom-reason-input"
                />
                <Button type="submit" size="sm" disabled={customReason.trim().length < 3} className="h-8 bg-[#00f0ff] text-black hover:bg-[#33f4ff]" data-testid="custom-reason-submit">
                  Regenerate
                </Button>
              </form>
            )}
            <button
              onClick={() => { setShowReasons(false); }}
              className="self-start text-[11px] text-slate-500 hover:text-slate-300 font-tab"
              data-testid="feedback-reasons-dismiss"
            >Just a thumbs-down, thanks</button>
          </div>
        )}
      </div>

      <TeacherNotes tab={tab} user={user} sharedNote={sharedNote} onNoteChange={setTeacherNote} />

      {tab.notes && (
        <div className="px-5 py-4 border-t border-white/10 bg-white/[0.02]">
          <p className="text-sm text-slate-400">
            <span className="text-[#00f0ff] font-tab text-xs uppercase tracking-widest mr-2">Tip</span>
            {tab.notes}
          </p>
        </div>
      )}

      <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
        <DialogContent className="bg-[#12161f] border-white/10 text-slate-100" data-testid="session-summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase tracking-tight flex items-center gap-2">
              <ClipboardList className="w-5 h-5 cyan-text" /> Session recap
            </DialogTitle>
            <DialogDescription className="text-slate-500">Here's what you drilled this session.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 flex items-center justify-between">
              <span className="text-sm text-slate-400 font-tab">Practiced this session</span>
              <span className="font-heading text-2xl cyan-text" data-testid="session-minutes">{fmtTime(sessionSecs)}</span>
            </div>
            <div>
              <h4 className="font-heading text-sm uppercase tracking-widest text-[#ffab00] mb-2">Loops drilled</h4>
              {sessionLoops.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="session-loops-empty">No A–B loops drilled yet this session.</p>
              ) : (
                <div className="space-y-1.5" data-testid="session-loops">
                  {sessionLoops.map((l, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                      <span className="text-sm truncate">{l.label}</span>
                      <span className="font-tab text-sm cyan-text">×{l.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-heading text-sm uppercase tracking-widest text-[#ffab00] mb-2">Songs practiced</h4>
              {sessionSongs.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="session-songs-empty">No songs played yet this session.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="session-songs">
                  {sessionSongs.map((s, i) => (
                    <Badge key={i} className="bg-white/5 text-slate-300 border-0 font-tab">{s}</Badge>
                  ))}
                </div>
              )}
            </div>
            {sessionHistory.length > 0 && (
              <div>
                <h4 className="font-heading text-sm uppercase tracking-widest text-slate-400 mb-2">Past sessions</h4>
                <div className="space-y-1.5 max-h-40 overflow-y-auto scroll-thin" data-testid="session-history">
                  {sessionHistory.map((h, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-xs">
                      <span className="text-slate-400 font-tab shrink-0">
                        {new Date(h.date).toLocaleDateString()} · {Math.round((h.secs || 0) / 60)}m
                      </span>
                      <span className="text-slate-500 truncate max-w-[55%] text-right">{(h.songs || []).join(", ") || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-2 pt-1">
              {user && (user.team_member || user.is_team_owner) && (
                <Button onClick={postToClass} className="w-full bg-[#ffab00] text-black hover:bg-[#ffc233] font-semibold" data-testid="post-to-class-button">
                  <Users className="w-4 h-4 mr-2" /> Post to my class feed
                </Button>
              )}
              <Button onClick={shareProgress} className="w-full bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-semibold" data-testid="session-share-button">
                <Share2 className="w-4 h-4 mr-2" /> Share my weekly progress
              </Button>
              <Button onClick={endSession} variant="outline" className="w-full border-white/15 bg-white/5" data-testid="end-session-button">
                <ClipboardList className="w-4 h-4 mr-2" /> End &amp; log this session
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
